import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../api";
import { useI18n } from "../i18n";
import { HERALDRY } from "../brand";

export default function Home() {
  const { t, lang } = useI18n();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-black text-white">
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERALDRY.knightsPainting} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/40" />
        </div>
        <div className="relative z-10 w-full max-w-[1600px] mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-32">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-bone mb-6">{t.hero.kicker}</div>
            <h1 className="font-black text-5xl sm:text-7xl lg:text-[8rem] leading-[0.85] tracking-tighter uppercase">
              {t.hero.line1}<br/><span className="text-blood">{t.hero.line2}</span>
            </h1>
            <p className="font-gothic text-4xl md:text-6xl mt-8 text-bone">{t.hero.line3}</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
              <Link to="/shop" data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-3 bg-blood text-white px-10 py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors group">
                {t.hero.cta_primary}<ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link to="/about" data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-10 py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors">
                {t.hero.cta_secondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="border-y border-white/10 bg-blood-deep py-5 overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap text-xs font-bold tracking-[0.3em] uppercase text-bone">
          {Array(8).fill(0).map((_, i) => (
            <span key={`marquee-${i}`} className="px-8 flex items-center gap-8">{t.marquee}<span className="text-blood">✟</span></span>
          ))}
        </div>
      </div>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-4">{t.featured.kicker}</div>
            <h2 className="font-black text-4xl md:text-6xl leading-none tracking-tighter uppercase">{t.featured.title}</h2>
          </div>
          <Link to="/shop" className="hidden md:flex link-hover text-xs font-bold tracking-[0.25em] uppercase">{t.nav.shop} →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {featured.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.1 }}>
              <Link to={`/product/${p.id}`} data-testid={`featured-product-${p.id}`} className="group block">
                <div className="aspect-[3/4] overflow-hidden bg-[#0a0707]">
                  <img src={p.image} alt={p.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out" />
                </div>
                <div className="pt-4">
                  <div className="text-sm font-bold tracking-wide">{lang === "fr" ? p.name_fr : p.name}</div>
                  <div className="text-xs text-bone mt-1">€{p.price.toFixed(0)}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0a0707] border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-32">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-16">{t.pillars.kicker}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {t.pillars.items.map((it, i) => (
              <motion.div key={it.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
                className="border-t border-blood pt-6">
                <div className="font-gothic text-blood text-3xl mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="text-lg font-bold mb-3">{it.t}</div>
                <p className="text-sm text-white/60 leading-relaxed">{it.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7 order-2 md:order-1">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-6">{t.about.kicker}</div>
          <h2 className="font-black text-4xl md:text-6xl leading-[0.9] tracking-tighter uppercase mb-8">{t.about.title}</h2>
          <p className="text-white/70 text-lg leading-relaxed mb-6 max-w-2xl">{t.about.body1}</p>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl">{t.about.body2}</p>
          <Link to="/about" className="inline-flex items-center gap-3 mt-10 text-xs font-bold tracking-[0.25em] uppercase link-hover">
            {t.hero.cta_secondary} →
          </Link>
        </div>
        <div className="md:col-span-5 order-1 md:order-2">
          <div className="aspect-[3/4] overflow-hidden">
            <img src={HERALDRY.knightsPainting} alt="Heritage" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 py-32">
        <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-12 text-center">{t.faq.kicker}</div>
        <div className="space-y-2">
          {t.faq.items.map((it, i) => <FaqItem key={it.q} q={it.q} a={it.a} idx={i} />)}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 opacity-20">
          <img src={HERALDRY.knightsPainting} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-blood-deep/40 to-black" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-48 text-center">
          <h2 className="font-gothic text-6xl md:text-9xl text-blood leading-none mb-8">{t.cta_footer.title}</h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto mb-12">{t.cta_footer.sub}</p>
          <Link to="/shop" data-testid="cta-final"
            className="inline-flex items-center gap-3 bg-white text-black px-12 py-6 text-xs font-bold tracking-[0.3em] uppercase hover:bg-bone transition-colors group">
            {t.cta_footer.btn}<ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button data-testid={`faq-toggle-${idx}`} onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-6 text-left hover:text-bone transition-colors">
        <span className="text-base md:text-lg font-medium">{q}</span>
        <span className="text-blood font-gothic text-2xl">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="pb-6 text-white/60 leading-relaxed">{a}</div>}
    </div>
  );
}
